import React, { useState, useEffect, useMemo } from "react";
import { TouchableOpacity, StyleSheet, ScrollView } from "react-native";
import { Text, View, Card, Wrapper } from "@components/atoms/Themed";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import { graphql, useFragment } from "react-relay/hooks";
import Spacer from "@components/atoms/Spacer";
import Icon from "@components/atoms/Icon";
import GradientButton from "@components/atoms/GradientButton";
import Divider from "@components/atoms/Divider";
import { ParentMediaType, ParentMediaTypeNames } from "@constants/App";
import SNSIcon from "@components/atoms/SNSIcon";
import Button from "@components/atoms/Button";
import { replace } from "@navigation/navigate";
import {
  CampaignFilter$key,
  CategoryTypes,
} from "@generated/CampaignFilter.graphql";
import useFilterStatus from "@hooks/useFilterStatus";

const categoryList = graphql`
  fragment CampaignFilter on Query {
    mCategories {
      id
      name
      keyName
    }
    prefectures {
      id
      name
      regionName
    }
  }
`;

export default function CampaignFilter({
  listFragment,
  tab,
}: {
  listFragment: CampaignFilter$key;
  tab: string;
}) {
  const { set: setFilter, state: filters } = useFilterStatus();
  const { mCategories, prefectures } = useFragment(categoryList, listFragment);
  const [selectedSnsTypes, setSelectedSnsTypes] = useState<ParentMediaType[]>(
    []
  );
  const [selectedCategories, setSelectedCategories] = useState<CategoryTypes[]>(
    []
  );
  const [selectedHasMoney, setSelectedHasMoney] = useState<boolean>(false);
  const [selectedHasProduct, setSelectedHasProduct] = useState<boolean>(false);
  const [selectedPrefectures, setSelectedPrefectures] = useState<string[]>([]);
  // 地域パネルの開閉状態（開いている地域名を保持）
  const [expandedRegions, setExpandedRegions] = useState<string[]>([]);

  useEffect(() => {
    if (filters !== null) {
      setSelectedCategories(filters.selectedCategories);
      setSelectedSnsTypes(filters.selectedSns);
      setSelectedHasMoney(filters.selectedHasMoney);
      setSelectedHasProduct(filters.selectedHasProduct);
      setSelectedPrefectures(filters.selectedPrefectures ?? []);
    }
  }, [filters]);

  const mMediaTypes: ParentMediaType[] = [
    "instagram",
    "twitter",
    "youtube",
    "tiktok",
    "cast",
    "other",
  ];

  function toggle(params: {
    selectedSns?: ParentMediaType;
    selectedCategory?: CategoryTypes;
    selectedMoney?: boolean;
    selectedProduct?: boolean;
    selectedPrefecture?: string;
  }): void {
    if (params.selectedSns !== undefined) {
      if (selectedSnsTypes.includes(params.selectedSns)) {
        setSelectedSnsTypes(
          selectedSnsTypes.filter((item) => item !== params.selectedSns)
        );
      } else {
        setSelectedSnsTypes([...selectedSnsTypes, params.selectedSns]);
      }
    } else if (params.selectedCategory !== undefined) {
      if (selectedCategories.includes(params.selectedCategory)) {
        setSelectedCategories(
          selectedCategories.filter((item) => item !== params.selectedCategory)
        );
      } else {
        setSelectedCategories([...selectedCategories, params.selectedCategory]);
      }
    } else if (params.selectedMoney !== undefined) {
      if (selectedHasMoney) {
        setSelectedHasMoney(false);
      } else {
        setSelectedHasMoney(true);
      }
    } else if (params.selectedProduct !== undefined) {
      if (selectedHasProduct) {
        setSelectedHasProduct(false);
      } else {
        setSelectedHasProduct(true);
      }
    } else if (params.selectedPrefecture !== undefined) {
      const pref = params.selectedPrefecture;
      if (selectedPrefectures.includes(pref)) {
        setSelectedPrefectures(
          selectedPrefectures.filter((item) => item !== pref)
        );
      } else {
        setSelectedPrefectures([...selectedPrefectures, pref]);
      }
    }
  }

  // 都道府県を地域ごとにグルーピング（resolverが地域順で返す）
  type PrefItem = (typeof prefectures)[number];
  const prefectureGroups = useMemo(() => {
    const groups: { region: string; items: PrefItem[] }[] = [];
    prefectures.forEach((pref) => {
      const last = groups[groups.length - 1];
      if (last && last.region === pref.regionName) {
        last.items.push(pref);
      } else {
        groups.push({ region: pref.regionName, items: [pref] });
      }
    });
    return groups;
  }, [prefectures]);

  // 地域パネルの開閉トグル
  function toggleExpand(region: string): void {
    setExpandedRegions((prev) =>
      prev.includes(region)
        ? prev.filter((name) => name !== region)
        : [...prev, region]
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView>
        <Wrapper>
          <Spacer height={24} />
          <View style={styles.content}>
            <View>
              <Text style={styles.title}>キャンペーンを行うSNS</Text>
              <Spacer height={4} />
              {mMediaTypes.map((item) => (
                <TouchableOpacity
                  key={`sns-${item}`}
                  onPress={() =>
                    toggle({
                      selectedSns: item,
                    })
                  }
                >
                  <Spacer height={12} />
                  <View style={styles.item}>
                    {selectedSnsTypes.includes(item) ? (
                      <Icon
                        color={Colors.green}
                        name="checkBoxFill"
                        size={24}
                      />
                    ) : (
                      <Icon
                        color={Colors.black}
                        name="checkboxEmpty"
                        size={24}
                      />
                    )}
                    <Spacer width={8} />
                    <SNSIcon name={item} style={{ height: 24, width: 24 }} />
                    <Spacer width={4} />
                    <Text style={styles.text}>
                      {ParentMediaTypeNames[item]}
                    </Text>
                  </View>
                  <Spacer height={12} />
                </TouchableOpacity>
              ))}
            </View>
            <Spacer height={12} />
            <Divider />
            <Spacer height={24} />
            <View>
              <Text style={styles.title}>キャンペーン商品のカテゴリー</Text>
              <Spacer height={4} />
              {mCategories.map((item) => (
                <TouchableOpacity
                  key={`category-${item.keyName}`}
                  onPress={() => toggle({ selectedCategory: item.keyName })}
                >
                  <Spacer height={12} />
                  <View style={styles.item}>
                    {selectedCategories.includes(item.keyName) ? (
                      <Icon
                        color={Colors.green}
                        name="checkBoxFill"
                        size={24}
                      />
                    ) : (
                      <Icon
                        color={Colors.black}
                        name="checkboxEmpty"
                        size={24}
                      />
                    )}
                    <Spacer width={8} />
                    <Text style={styles.text}>{item.name}</Text>
                  </View>
                  <Spacer height={12} />
                </TouchableOpacity>
              ))}
            </View>
            <Spacer height={12} />
            <Divider />
            <Spacer height={24} />
            <View>
              <Text style={styles.title}>報酬</Text>

              <TouchableOpacity onPress={() => toggle({ selectedMoney: true })}>
                <Spacer height={12} />
                <View style={styles.item}>
                  {selectedHasMoney ? (
                    <Icon color={Colors.green} name="checkBoxFill" size={24} />
                  ) : (
                    <Icon color={Colors.black} name="checkboxEmpty" size={24} />
                  )}
                  <Spacer width={8} />
                  <Text style={styles.text}>報酬金あり</Text>
                </View>
                <Spacer height={12} />
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => toggle({ selectedProduct: true })}
              >
                <Spacer height={12} />
                <View style={styles.item}>
                  {selectedHasProduct ? (
                    <Icon color={Colors.green} name="checkBoxFill" size={24} />
                  ) : (
                    <Icon color={Colors.black} name="checkboxEmpty" size={24} />
                  )}
                  <Spacer width={8} />
                  <Text style={styles.text}>商品提供あり</Text>
                </View>
                <Spacer height={12} />
              </TouchableOpacity>
              <Spacer height={12} />
            </View>
            <Divider />
            <Spacer height={24} />
            {/* 体験するエリア（来店型キャンペーン向け・地域グルーピング） */}
            <View>
              <Text style={styles.title}>体験するエリア</Text>
              <Spacer height={4} />
              {prefectureGroups.map((group) => {
                const isExpanded = expandedRegions.includes(group.region);
                const selectedCount = group.items.filter((item) =>
                  selectedPrefectures.includes(item.name)
                ).length;
                return (
                  <View key={`region-${group.region}`} style={styles.regionPanel}>
                    <TouchableOpacity
                      testID={`prefecture-region-${group.region}`}
                      onPress={() => toggleExpand(group.region)}
                    >
                      <View style={styles.regionHeader}>
                        <Text style={styles.region}>
                          {group.region}
                          {selectedCount > 0 ? `（${selectedCount}）` : ""}
                        </Text>
                        <Icon
                          color={Colors.black}
                          name={isExpanded ? "up" : "down"}
                          size={24}
                        />
                      </View>
                    </TouchableOpacity>
                    {isExpanded &&
                      group.items.map((item) => (
                        <TouchableOpacity
                          key={`prefecture-${item.id}`}
                          testID={`prefecture-${item.name}`}
                          onPress={() =>
                            toggle({ selectedPrefecture: item.name })
                          }
                        >
                          <Spacer height={8} />
                          <View style={styles.regionItem}>
                            {selectedPrefectures.includes(item.name) ? (
                              <Icon
                                color={Colors.green}
                                name="checkBoxFill"
                                size={24}
                              />
                            ) : (
                              <Icon
                                color={Colors.black}
                                name="checkboxEmpty"
                                size={24}
                              />
                            )}
                            <Spacer width={8} />
                            <Text style={styles.text}>{item.name}</Text>
                          </View>
                          <Spacer height={8} />
                        </TouchableOpacity>
                      ))}
                  </View>
                );
              })}
            </View>
          </View>
        </Wrapper>
      </ScrollView>
      <Card>
        <Wrapper>
          <View style={styles.buttons}>
            <View style={styles.button}>
              <Button
                onPress={() => {
                  setFilter({
                    selectedCategories: [],
                    selectedSns: [],
                    selectedHasMoney: false,
                    selectedHasProduct: false,
                    selectedPrefectures: [],
                    filterStatus: false,
                  });
                  replace("Root", {
                    screen: "Home",
                    params: {
                      tab,
                    },
                  });
                }}
                textStyle={styles.buttonText}
                title="リセット"
                width="100%"
              />
            </View>

            <Spacer width={8} />

            <View style={styles.button}>
              <GradientButton
                testID="filter-apply"
                onPress={() => {
                  setFilter({
                    selectedCategories,
                    selectedSns: selectedSnsTypes,
                    selectedHasMoney,
                    selectedHasProduct,
                    selectedPrefectures,
                    filterStatus: true,
                  });
                  replace("Root", {
                    screen: "Home",
                    params: {
                      tab,
                    },
                  });
                }}
                textStyle={styles.buttonText}
                title="適用"
                width="100%"
              />
            </View>
          </View>
        </Wrapper>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
  },
  title: {
    ...Fonts.xlb100,
  },
  item: {
    flexDirection: "row",
  },
  regionPanel: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.gray30,
  },
  regionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 14,
  },
  regionItem: {
    flexDirection: "row",
    paddingLeft: 16,
  },
  region: {
    ...Fonts.xlb100,
  },
  text: {
    textAlign: "center",
    ...Fonts.xlr160,
  },
  buttons: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
  },
  button: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  buttonText: {
    ...Fonts.xlb100,
  },
});
